/**
 * The "Prepping ship…" hold (M10 P1b3, hull tiers spec §6.5, plan 2026-10-10-m10-p1 §3.3): with ship streaming on
 * (`ship-stream.json enabled`) boot loads only the Kestrel among ship models, so you could board a hull whose GLB
 * hasn't landed. You never fly it as a stand-in:
 *
 * - **The hold.** Per frame (`beforeTicks`, allocation-free): when you're in space (not docked, no title screen) and
 *   the flown def's model isn't loaded, the `prepping` hold stops the sim, *"Prepping ship…"* shows in the overlay
 *   layer and the stream is asked for the model. When it lands the hold goes. After `prepTimeoutS` (or a failed load)
 *   the message becomes *"Your ship's model failed to load"* with Retry, which asks again. There is no fallback to
 *   flying a stand-in. Docked, the hold never shows: Launch is where it starts.
 * - **The warm set.** At career start and on every hangar change (message rate: a reference compare per frame), the
 *   stream is asked for the flown def, the hangar's defs (`career.hangar[].defId`) and the raider pair
 *   (`encounters.json repo.ships`), so a switch at the Hangar or a repo crew finds its model already in.
 *
 * Career mode only, and only when boot provided the `ships` service. The transitions (enter, fail, retry, the warm
 * set) allocate; the per-frame steps don't (D-014, `feature.test.ts` hot-loop scan).
 */
import { FEATURE_ORDER, type FrameInfo, type GameContext, type GameFeature } from '../../app/feature'
import { loadShipStreamConfig, type ShipDefAsset, type ShipStream } from '../../assets/shipStream'
import { HOLD } from '../../career/runtime'
import type { Career } from '../../career/types'
import { logger } from '../../core/logger'
import encountersJson from '../../data/encounters.json'
import { encountersFileSchema } from '../../data/m2Schemas'
import type { Sim } from '../../sim'
import type { M2Runtime } from '../../space/contracts'
import { Ship } from '../../world/components'

/** The hold reason while the flown model loads. */
export const PREP_HOLD = 'prepping'

/** Player-facing lines. */
export const PREP_TEXTS = {
  prepping: 'Prepping ship…',
  failed: "Your ship's model failed to load",
  retry: 'Retry',
} as const

export type PrepState = 'idle' | 'prepping' | 'failed'

/** What the hold shows (the DOM view, or a stub in tests). */
export interface PrepView {
  show(state: Exclude<PrepState, 'idle'>): void
  hide(): void
}

/** The stream calls the hold needs (structural, so tests can stub it). */
export type PrepShips = Pick<ShipStream, 'get' | 'ensure'>

export interface PrepHold {
  readonly sim: Pick<Sim, 'playerEid' | 'defs'>
  readonly ships: PrepShips
  readonly holds: Set<string>
  readonly view: PrepView
  /** Seconds before the hold offers Retry (`ship-stream.json prepTimeoutS`). */
  readonly timeoutS: number
  state: PrepState
  /** The asset the hold waits for (null when idle). */
  asset: string | null
  /** Real seconds waited since the hold began or Retry was pressed. */
  waitedS: number
  /** The last `ensure` for `asset` rejected. */
  loadFailed: boolean
  /**
   * The last `ensure` for this asset resolved. If the stream still doesn't hold it (an id it doesn't stream), the
   * hold lets go rather than lock the game.
   */
  settledAsset: string | null
}

export function createPrepHold(o: Pick<PrepHold, 'sim' | 'ships' | 'holds' | 'view' | 'timeoutS'>): PrepHold {
  return { ...o, state: 'idle', asset: null, waitedS: 0, loadFailed: false, settledAsset: null }
}

/**
 * Per frame (not paused): the asset the hold must wait for, or null. In space only (`blocked` = docked or the title
 * screen), for the player's live def slot (the career rewrites it in place when you board another hull).
 */
export function missingFlownAsset(h: PrepHold, blocked: boolean): string | null {
  if (blocked) return null
  const p = h.sim.playerEid
  if (p < 0) return null
  const def = h.sim.defs[Ship.defIndex[p]]
  if (def === undefined) return null
  const asset = def.assetId
  if (h.ships.get(asset) !== undefined || h.settledAsset === asset) return null
  return asset
}

/** Per frame (not paused): adds, keeps, escalates or lifts the hold. A quiet frame is a few compares. */
export function stepPrepHold(h: PrepHold, dt: number, blocked: boolean): void {
  const asset = missingFlownAsset(h, blocked)
  if (asset === null) {
    if (h.state !== 'idle') endPrep(h)
    return
  }
  if (h.state === 'idle' || asset !== h.asset) {
    beginPrep(h, asset)
    return
  }
  h.waitedS += dt
  if (h.state === 'prepping' && (h.loadFailed || h.waitedS >= h.timeoutS)) failPrep(h)
}

/** Rare: the hold begins (or follows a new flown asset) and asks the stream for the model. */
function beginPrep(h: PrepHold, asset: string): void {
  h.asset = asset
  h.state = 'prepping'
  h.holds.add(PREP_HOLD)
  h.view.show('prepping')
  requestFlown(h, asset)
}

/** Rare: (re)starts the load of the flown model; the outcome is read by the next frames. */
function requestFlown(h: PrepHold, asset: string): void {
  h.waitedS = 0
  h.loadFailed = false
  h.ships.ensure([asset]).then(
    () => {
      if (h.ships.get(asset) === undefined) {
        logger.warn(`ship stream: ${asset} is not a streamed ship model; the prepping hold lets go`)
        h.settledAsset = asset
      }
    },
    () => {
      // The stream already logged it; the hold shows Retry on the next frame.
      if (h.asset === asset) h.loadFailed = true
    },
  )
}

/** Rare: the load timed out or failed; Retry is offered. */
function failPrep(h: PrepHold): void {
  h.state = 'failed'
  h.view.show('failed')
  logger.warn(`ship stream: the flown model ${h.asset ?? '?'} did not load within ${h.timeoutS} s`)
}

/** Rare: the model is in (or the player docked); the hold goes. */
function endPrep(h: PrepHold): void {
  h.state = 'idle'
  h.asset = null
  h.waitedS = 0
  h.loadFailed = false
  h.holds.delete(PREP_HOLD)
  h.view.hide()
}

/** The Retry button: asks the stream again and goes back to "Prepping ship…". */
export function retryPrep(h: PrepHold): void {
  if (h.state !== 'failed' || h.asset === null) return
  h.state = 'prepping'
  h.view.show('prepping')
  requestFlown(h, h.asset)
}

// ---------------------------------------------------------------------------------------------------------------
// The warm set
// ---------------------------------------------------------------------------------------------------------------

/** The def ids the stream should hold for this career: the flown def, the hangar's defs and the raider pair. */
export function warmDefIds(career: Pick<Career, 'ship' | 'hangar'>, raiderDefIds: readonly string[]): string[] {
  const ids = [career.ship.defId]
  for (const s of career.hangar ?? []) ids.push(s.defId)
  ids.push(...raiderDefIds)
  return ids
}

/** The raider pair (`encounters.json repo.ships`). */
export function raiderDefIds(raw: unknown = encountersJson): string[] {
  return encountersFileSchema.parse(raw).repo.ships.map((s) => s.ship)
}

export interface WarmSet {
  readonly ships: Pick<ShipStream, 'request' | 'assetsOfDefs'>
  /** Def id → asset id, captured at init (before any boarding rewrites the player's slot). */
  readonly defs: readonly ShipDefAsset[]
  readonly raiders: readonly string[]
  lastCareer: Career | null
  lastHangar: Career['hangar'] | null
  lastShipDef: string | null
}

export function createWarmSet(ships: WarmSet['ships'], defs: readonly ShipDefAsset[], raiders: readonly string[]): WarmSet {
  return { ships, defs, raiders, lastCareer: null, lastHangar: null, lastShipDef: null }
}

/**
 * Per frame: when the career snapshot changed and its hangar or flown def differs from the last one warmed, asks the
 * stream for the warm set. Skipped while the title screen holds (no career yet; boot fetches no ship but the Kestrel).
 */
export function stepWarmSet(w: WarmSet, career: Career, titled: boolean): void {
  if (titled || career === w.lastCareer) return
  w.lastCareer = career
  if (career.hangar === w.lastHangar && career.ship.defId === w.lastShipDef) return
  refreshWarmSet(w, career)
}

/** Rare: the career started or its hangar changed. */
function refreshWarmSet(w: WarmSet, career: Career): void {
  w.lastHangar = career.hangar
  w.lastShipDef = career.ship.defId
  w.ships.request(w.ships.assetsOfDefs(warmDefIds(career, w.raiders), w.defs))
}

// ---------------------------------------------------------------------------------------------------------------
// The view
// ---------------------------------------------------------------------------------------------------------------

/**
 * The hold's DOM: a centred card in the overlay layer, inserted right after `#overlay` (as the maps do): `#overlay` is
 * hidden in flight and M1's overlays rewrite it wholesale. The card hides while `#overlay` shows (the pause menu), and
 * `show` re-inserts it if something dropped it. DOM writes happen only on state changes.
 */
export function createPrepView(root: HTMLElement, onRetry: () => void): PrepView & { readonly el: HTMLElement } {
  const el = document.createElement('div')
  el.className = 'ship-prep'
  el.setAttribute('role', 'status')
  el.setAttribute('aria-live', 'polite')
  el.style.cssText =
    'position:fixed;left:50%;top:50%;transform:translate(-50%,-50%);z-index:30;padding:16px 24px;' +
    'background:rgba(8,12,18,0.82);border:1px solid rgba(160,190,220,0.35);color:#dfe8f2;' +
    'font:500 15px/1.4 system-ui,sans-serif;text-align:center;pointer-events:auto'
  const text = document.createElement('p')
  text.className = 'ship-prep-text'
  text.style.margin = '0'
  const retry = document.createElement('button')
  retry.type = 'button'
  retry.className = 'ship-prep-retry'
  retry.textContent = PREP_TEXTS.retry
  retry.style.marginTop = '10px'
  retry.addEventListener('click', onRetry)
  el.append(text, retry)
  el.hidden = true
  const style = document.createElement('style')
  style.textContent = '#overlay:not([hidden]) ~ .ship-prep { display: none; } .ship-prep[hidden], .ship-prep [hidden] { display: none; }'
  el.append(style)
  return {
    el,
    show(state): void {
      if (!el.isConnected) {
        if (root.parentElement) root.insertAdjacentElement('afterend', el)
        else root.appendChild(el)
      }
      text.textContent = state === 'failed' ? PREP_TEXTS.failed : PREP_TEXTS.prepping
      retry.hidden = state !== 'failed'
      el.hidden = false
    },
    hide(): void {
      el.hidden = true
    },
  }
}

// ---------------------------------------------------------------------------------------------------------------
// The feature
// ---------------------------------------------------------------------------------------------------------------

/** After the career (150) and the collider binder (152), so a switch committed this frame is seen this frame. */
const ORDER = FEATURE_ORDER.career + 3

let hold: PrepHold | null = null
let warm: WarmSet | null = null
let m2: M2Runtime | null = null
let view: (PrepView & { readonly el: HTMLElement }) | null = null

const feature: GameFeature = {
  name: 'shipStream',
  order: ORDER,
  init(ctx: GameContext): void {
    if (ctx.mode !== 'career') return
    const ships = ctx.services.maybe('ships')
    if (ships === undefined) return
    const cfg = loadShipStreamConfig()
    view = createPrepView(ctx.overlayRoot, () => {
      if (hold !== null) retryPrep(hold)
    })
    hold = createPrepHold({ sim: ctx.sim, ships, holds: ctx.holds, view, timeoutS: cfg.prepTimeoutS })
    m2 = ctx.services.maybe('m2') ?? null
    const defs = ctx.sim.defs.map((d) => ({ id: d.id, assetId: d.assetId }))
    warm = createWarmSet(ships, defs, raiderDefIds())
  },
  beforeTicks(ctx: GameContext, f: FrameInfo): void {
    if (hold === null) return
    const titled = ctx.holds.has(HOLD.title)
    if (warm !== null && m2 !== null) stepWarmSet(warm, m2.career(), titled)
    stepPrepHold(hold, f.dt, titled || ctx.holds.has(HOLD.docked))
  },
  dispose(): void {
    if (hold !== null && hold.state !== 'idle') hold.holds.delete(PREP_HOLD)
    view?.el.remove()
    hold = null
    warm = null
    m2 = null
    view = null
  },
}

export default feature
